import { useState } from "react";
import { Camera, Copy, Share2, Loader2, ExternalLink } from "lucide-react";
import { uploadFinishedPhoto } from "@/lib/api";
import { toast } from "sonner";

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file);
  });

export const SharePorch = ({ booking, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const shareUrl = `${window.location.origin}/share/${booking.reference}`;
  const hasPhoto = Boolean(booking.finished_photo_base64);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Please upload a photo under 4 MB");
    setBusy(true);
    try {
      const b64 = await readAsBase64(f);
      await uploadFinishedPhoto({ email: booking.email, reference: booking.reference, photo_base64: b64 });
      onUpdated({ ...booking, finished_photo_base64: b64 });
      toast.success("Your porch photo is live");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    const text = `Look at our porch styled by Pumpkin & Porch — the ${booking.collection_name} collection!`;
    if (navigator.share) {
      try { await navigator.share({ title: "Our autumn porch", text, url: shareUrl }); return; } catch { /* cancelled */ }
    }
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied");
  };
  const copy = async () => { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };

  return (
    <div className="p-8 border-t" style={{ borderColor: "var(--pp-border)" }} data-testid="share-section">
      <div className="pp-caption mb-1">Share your porch</div>
      <p className="text-sm mb-5" style={{ color: "var(--pp-text-muted)" }}>
        {hasPhoto ? "Your finished porch is ready to show off." : "Once we've styled your porch, upload a photo and share it with friends."}
      </p>
      {hasPhoto && <img src={booking.finished_photo_base64} alt="Finished porch" className="max-h-72 mb-5" data-testid="finished-photo" />}
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="finished-photo" className="pp-btn cursor-pointer" style={{ backgroundColor: hasPhoto ? "var(--pp-espresso)" : "var(--pp-terracotta)" }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} {hasPhoto ? "Replace photo" : "Upload finished photo"}
        </label>
        <input id="finished-photo" data-testid="finished-photo-input" type="file" accept="image/*" className="hidden" onChange={onFile} />
        {hasPhoto && (
          <>
            <button data-testid="share-button" onClick={share} className="pp-btn" style={{ backgroundColor: "var(--pp-sage)" }}><Share2 className="h-4 w-4" /> Share</button>
            <button data-testid="copy-link-button" onClick={copy} className="pp-btn-ghost" style={{ color: "var(--pp-brown)" }}><Copy className="h-4 w-4" /> Copy link</button>
            <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="view-share-page" className="pp-btn-ghost" style={{ color: "var(--pp-brown)" }}><ExternalLink className="h-4 w-4" /> View page</a>
          </>
        )}
      </div>
    </div>
  );
};
